import _ from 'underscore';
import { applyPartialConversionColumnName } from '@bingads-webui-campaign/column-conversions';
import { AccountsMode } from './constants';
import { cardPerformanceId, cardTopCampaignsId } from '../../fluent-page-consts';

const allMetrics = (permissions = {}, accountsMode = AccountsMode.Expert, campaignType = null) => {
  const { AppCampaignNewCampaignGridColumns } = permissions.dynamic || {};
  const isAppCampaign = AppCampaignNewCampaignGridColumns && (campaignType === 128 || campaignType === 'App');
  const isAllCampaigns = AppCampaignNewCampaignGridColumns && campaignType === 65535;
  if (accountsMode === AccountsMode.Expert) {
    return applyPartialConversionColumnName(
      permissions,
      _.compact([
        'Clicks',
        'Impressions',
        'CTR',
        'AverageCPC',
        'Spend',
        !permissions.IsAvgPositionDeprecationPhase2Enabled && 'AveragePosition',
        'TopImpressionRate',
        'AbsoluteTopImpressionRate',
        'Conversions',
        'ConversionRate',
        'CPA',
        'AdvertiserReportedRevenue',
        'RevenueOnAdSpend',
        (isAppCampaign || isAllCampaigns) && 'Downloads',
        isAppCampaign && 'PostClickDownloadRate',
        isAppCampaign && 'CostPerDownload',
        (isAppCampaign || isAllCampaigns) && 'FirstLaunches',
        isAppCampaign && 'PostClickFirstLaunchRate',
        (isAppCampaign || isAllCampaigns) && 'CostPerFirstLaunch',
        isAppCampaign && 'Purchases',
        isAppCampaign && 'PostInstallPurchaseRate',
        isAppCampaign && 'CostPerPurchase',
        isAppCampaign && 'Subscriptions',
        isAppCampaign && 'PostInstallSubscriptionRate',
        isAppCampaign && 'CostPerSubscription',
      ])
    );
  }

  return [
    'Clicks',
    'Impressions',
    'CTR',
    'AverageCPC',
    'Spend',
    'Conversions',
    'ConversionRate',
    'CPA',
  ];
};

// Do we need a full list like this?
// https://msasg.visualstudio.com/Bing_Ads/_git/AdsAppsCampaignUI?path=/private/component-new-grid/packages/campaignui-common-columns/src/strings.js&_a=contents&version=GBmaster
const metricToDisplayNameMap = {
  Clicks: _TL_('Clicks'),
  Impressions: _TL_('Impr.'),
  CTR: _TL_('CTR'),
  AverageCPC: _TL_('Avg. CPC'),
  Spend: _TL_('Spend'),
  AveragePosition: _TL_('Avg. pos.'),
  TopImpressionRate: _TL_('Top impr. rate'),
  AbsoluteTopImpressionRate: _TL_('Abs. top impr. rate'),
  Conversions: _TL_('Conv.'),
  ConversionRate: _TL_('Conv. rate'),
  CPA: _TL_('CPA'),
  AdvertiserReportedRevenue: _TL_('Revenue'),
  RevenueOnAdSpend: _TL_('Return on ad spend'),
  Downloads: _TL_('Downloads'),
  PostClickDownloadRate: _TL_('Post-click download rate'),
  CostPerDownload: _TL_('Cost per download'),
  FirstLaunches: _TL_('Installs'),
  PostClickFirstLaunchRate: _TL_('Post-click install rate'),
  CostPerFirstLaunch: _TL_('Cost per install'),
  Purchases: _TL_('Purchases'),
  PostInstallPurchaseRate: _TL_('Post-install purchase rate'),
  CostPerPurchase: _TL_('Cost per purchase'),
  Subscriptions: _TL_('Subscriptions'),
  PostInstallSubscriptionRate: _TL_('Post-install subscription rate'),
  CostPerSubscription: _TL_('Cost per subscription'),

  // when dynamic PartialConversions & ColumnConversion are true
  ConversionsCredit: _TL_('Conv.'),
  PartialConversionRate: _TL_('Conv. rate'),
  PartialConversionCPA: _TL_('CPA'),
};

const UIToMTMetricNameMap = {
  Impressions: 'Impression',
  Clicks: 'Click',
  Spend: 'Spend',
};

const metricSelect = (permissions, accountsMode = AccountsMode.Expert, campaignType) => _.map(
  allMetrics(permissions, accountsMode, campaignType),
  metric => `PerformanceMetrics/${metric}`
).join(',');

const isInsightsMetric = UIMetric => _.contains(_.keys(UIToMTMetricNameMap), UIMetric);

const getInsightsMetric = (allowedMetric, UIMetric) =>
  _.filter(allowedMetric, MTMetric => MTMetric === UIToMTMetricNameMap[UIMetric]);

const getInsightsMetricFilterOption = metric => `Enum.FluctuationType'${UIToMTMetricNameMap[metric]}'`;

const defaultMetrics = (permissions = {}, accountsMode = AccountsMode.Expert) => {
  const metrics = ['Spend', 'Clicks', 'Conversions', 'Impressions'];

  if (accountsMode === AccountsMode.Expert) {
    return applyPartialConversionColumnName(
      permissions,
      metrics
    );
  }

  return metrics;
};

/**
 * This function filters unsupported metrics from dropdowns on the overview page, based on card and campaign types.
 * This ensures users can only select valid metrics, avoiding multiple API calls per page.
 */
const getUIMetrics = (metrics, campaignType = null, cardTypeId = null) => {
  const shouldExcludeAppMetrics = cardTypeId !== cardPerformanceId && cardTypeId !== cardTopCampaignsId;
  const isAllCampaignsScope = campaignType === 65535;
  const excludedMetrics = ['Downloads', 'FirstLaunches', 'CostPerFirstLaunch'];
  if (isAllCampaignsScope && shouldExcludeAppMetrics) {
    return metrics.filter(m => !excludedMetrics.includes(m.key));
  }
  return metrics;
};

export {
  allMetrics,
  defaultMetrics,
  metricToDisplayNameMap,
  metricSelect,
  isInsightsMetric,
  getInsightsMetric,
  getInsightsMetricFilterOption,
  getUIMetrics,
};
